import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import { PageHero } from "@/components/PageHero";
import {
    BookHeart, Sparkles, History, Activity, Pill, CalendarCheck,
    FolderOpen, User, Plus, ArrowRight, AlertTriangle, StickyNote,
    Map as MapIcon, Trash2, Save, Loader2, Lock,
} from "lucide-react";
import Behavior from "./Behavior";
import Documents from "./Documents";
import CareTeamSummary from "./CareTeamSummary";
import PatientProfile from "./PatientProfile";

const SECTIONS = [
    { key: "overview", label: "Overview", icon: Sparkles },
    { key: "timeline", label: "Timeline", icon: History },
    { key: "behaviors", label: "Behaviors & Changes", icon: Activity },
    { key: "medications", label: "Medications", icon: Pill },
    { key: "appointments", label: "Appointment Prep", icon: CalendarCheck },
    { key: "documents", label: "Documents", icon: FolderOpen },
    { key: "about", label: "About Your Loved One", icon: User },
];

const CAT_META = {
    behavior: { icon: Activity, chip: "bg-coral-light text-coral-dark", label: "Behavior" },
    appointment: { icon: CalendarCheck, chip: "bg-[#DFE8FC] text-[#2D62D9]", label: "Appointment" },
    document: { icon: FolderOpen, chip: "bg-stage_middle/40 text-stage_middle-dark", label: "Document" },
    medication: { icon: Pill, chip: "bg-stage_late/30 text-stage_late-dark", label: "Medication" },
    roadmap: { icon: MapIcon, chip: "bg-teal-soft text-teal-dark", label: "Roadmap" },
    note: { icon: StickyNote, chip: "bg-cream-dark text-[#4C5551]", label: "Note" },
};

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
};

export default function Journey() {
    const { section = "overview" } = useParams();
    const active = SECTIONS.some(s => s.key === section) ? section : "overview";

    return (
        <div className="space-y-6 animate-fade-in-up" data-testid="journey-page">
            <nav className="flex gap-1.5 flex-wrap" data-testid="journey-section-nav">
                {SECTIONS.map(s => (
                    <NavLink
                        key={s.key}
                        to={s.key === "overview" ? "/journey" : `/journey/${s.key}`}
                        end
                        data-testid={`journey-nav-${s.key}`}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm font-medium whitespace-nowrap transition-colors ${active === s.key ? "bg-[#4F7F95] text-white" : "bg-white border border-cream-dark text-[#4C5551] hover:border-[#4F7F95]"}`}
                    >
                        <s.icon size={15} strokeWidth={1.7} /> {s.label}
                    </NavLink>
                ))}
            </nav>

            {active === "overview" && <JourneyOverview />}
            {active === "timeline" && <JourneyTimeline />}
            {active === "behaviors" && <Behavior />}
            {active === "medications" && <MedicationsSection />}
            {active === "appointments" && <CareTeamSummary />}
            {active === "documents" && <Documents />}
            {active === "about" && <PatientProfile />}
        </div>
    );
}

/* ------------------------------------------------------------------ */
function JourneyOverview() {
    const sid = getSessionId();
    const [profile, setProfile] = useState({});
    const [pp, setPp] = useState({});
    const [appts, setAppts] = useState([]);
    const [events, setEvents] = useState([]);
    const [carePlan, setCarePlan] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let on = true;
        Promise.all([
            api.get(`/profile/${sid}`).catch(() => ({ data: {} })),
            api.get(`/patient-profile/${sid}`).catch(() => ({ data: {} })),
            api.get(`/appointment/${sid}`).catch(() => ({ data: [] })),
            api.get(`/journey/${sid}/timeline?limit=5`).catch(() => ({ data: { events: [] } })),
            api.get(`/care-plan/${sid}`).catch(() => ({ data: { plans: [] } })),
        ]).then(([pr, ppr, ar, tr, cp]) => {
            if (!on) return;
            setProfile(pr.data || {});
            setPp(ppr.data || {});
            setAppts(Array.isArray(ar.data) ? ar.data : []);
            setEvents(tr.data?.events || []);
            setCarePlan((cp.data?.plans || [])[0] || null);
        }).finally(() => on && setLoaded(true));
        return () => { on = false; };
    }, [sid]);

    const name = profile.loved_one_name || pp?.demographics?.preferred_name || pp?.demographics?.full_name || "";
    const diagnosis = pp?.demographics?.diagnosis || "";
    const stage = profile.stage || "";
    const meds = (pp?.medications || []).filter(m => (m.status || "active") !== "stopped");
    const allergies = [pp?.allergies?.medications, pp?.allergies?.food, pp?.allergies?.environmental].filter(Boolean).join(" · ");
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = appts
        .filter(a => (a.appointment_date || "") >= today)
        .sort((a, b) => (a.appointment_date || "").localeCompare(b.appointment_date || ""))[0];

    return (
        <div className="space-y-6" data-testid="journey-overview">
            <PageHero
                kicker="Our Journey"
                kickerIcon={BookHeart}
                title={name ? `${name}'s journey` : "Our Journey"}
                subtitle="What has been happening — everything you're keeping track of, in one place. Not a medical chart; your family's living story."
                actions={
                    <Link to="/journey/behaviors" className="ml-btn-primary text-sm !min-h-[44px] !py-2.5" data-testid="journey-quick-add">
                        <Plus size={16} strokeWidth={2} /> Add something from today
                    </Link>
                }
            />

            {!loaded ? (
                <div className="ml-card p-8 text-center text-sm text-[#6E7A74]">Gathering the story so far…</div>
            ) : (
                <>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="ml-card p-5 bg-white" data-testid="journey-overview-loved-one">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E7A74]">Your loved one</p>
                            <p className="mt-1 text-lg font-heading font-semibold text-[#1F2624]">{name || "Add their name"}</p>
                            <p className="mt-0.5 text-sm text-[#4C5551]">
                                {diagnosis || "Diagnosis or evaluation status not added yet"}
                                {stage ? ` · ${stage} stage` : ""}
                            </p>
                            <Link to="/journey/about" className="mt-2 inline-flex items-center gap-1 text-sm text-teal-dark font-medium hover:underline">
                                About your loved one <ArrowRight size={14} />
                            </Link>
                        </div>
                        <div className="ml-card p-5 bg-white" data-testid="journey-overview-appointment">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E7A74]">Next appointment</p>
                            {upcoming ? (
                                <>
                                    <p className="mt-1 text-lg font-heading font-semibold text-[#1F2624]">{upcoming.provider_name || "Visit"}</p>
                                    <p className="mt-0.5 text-sm text-[#4C5551]">{fmtDate(upcoming.appointment_date)}</p>
                                </>
                            ) : (
                                <p className="mt-1 text-sm text-[#4C5551]">Nothing scheduled yet.</p>
                            )}
                            <Link to="/journey/appointments" className="mt-2 inline-flex items-center gap-1 text-sm text-teal-dark font-medium hover:underline">
                                Prepare for a visit <ArrowRight size={14} />
                            </Link>
                        </div>
                        <div className="ml-card p-5 bg-white" data-testid="journey-overview-meds">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6E7A74]">Current medications</p>
                            {meds.length ? (
                                <ul className="mt-1.5 space-y-1">
                                    {meds.slice(0, 4).map((m, i) => (
                                        <li key={i} className="text-sm text-[#1F2624]">{m.name}{m.dose ? ` — ${m.dose}` : ""}</li>
                                    ))}
                                    {meds.length > 4 && <li className="text-xs text-[#6E7A74]">+ {meds.length - 4} more</li>}
                                </ul>
                            ) : (
                                <p className="mt-1 text-sm text-[#4C5551]">No medications tracked yet.</p>
                            )}
                            <Link to="/journey/medications" className="mt-2 inline-flex items-center gap-1 text-sm text-teal-dark font-medium hover:underline">
                                Manage medications <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>

                    {carePlan && (
                        <div className="ml-card p-5 bg-white border-l-4 border-l-teal" data-testid="journey-care-plan">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-dark">
                                From your care team — {carePlan.org_name || "your organization"}
                            </p>
                            <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                {[["recommendations", "Today's recommendations"], ["medication_changes", "Medication changes"],
                                ["safety", "Safety"], ["referrals", "Referrals"], ["goals", "Goals"], ["next_follow_up", "Next follow-up"]]
                                    .filter(([k]) => carePlan[k])
                                    .map(([k, label]) => (
                                        <div key={k}>
                                            <p className="text-xs font-semibold text-[#6E7A74]">{label}</p>
                                            <p className="text-sm text-[#1F2624] whitespace-pre-wrap">{carePlan[k]}</p>
                                        </div>
                                    ))}
                            </div>
                            {carePlan.updated_at && (
                                <p className="mt-2 text-[11px] text-[#6E7A74]">Updated {fmtDate(carePlan.updated_at)} by {carePlan.updated_by}</p>
                            )}
                        </div>
                    )}

                    {allergies && (
                        <div className="ml-card p-4 bg-coral-light/40 border border-coral/30 flex items-start gap-3" data-testid="journey-overview-alerts">
                            <AlertTriangle size={18} className="text-coral-dark mt-0.5 shrink-0" strokeWidth={1.8} />
                            <p className="text-sm text-[#4C5551]"><strong className="text-[#1F2624]">Important to remember:</strong> allergies — {allergies}</p>
                        </div>
                    )}

                    <div className="ml-card p-5 bg-white" data-testid="journey-overview-recent">
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-[#1F2624]">Recent changes</p>
                            <Link to="/journey/timeline" className="text-sm text-teal-dark font-medium hover:underline inline-flex items-center gap-1">
                                Full timeline <ArrowRight size={14} />
                            </Link>
                        </div>
                        {events.length ? (
                            <ul className="mt-3 space-y-2.5">
                                {events.map((e, i) => <TimelineRow key={e.id || i} event={e} compact />)}
                            </ul>
                        ) : (
                            <p className="mt-2 text-sm text-[#4C5551]">Nothing logged yet. When you note a behavior, add a medication, or upload a document, it appears here.</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
function TimelineRow({ event, compact = false }) {
    const meta = CAT_META[event.category] || CAT_META.note;
    const Icon = meta.icon;
    return (
        <li className={`flex items-start gap-3 ${compact ? "" : "border border-cream-dark rounded-2xl p-4 bg-white"}`}>
            <span className={`mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${meta.chip}`}>
                <Icon size={15} strokeWidth={1.8} />
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1F2624]">{event.what}</p>
                {event.detail && <p className="text-xs text-[#4C5551] mt-0.5 truncate">{event.detail}</p>}
                <p className="text-[11px] text-[#6E7A74] mt-0.5">
                    {fmtDate(event.date)} · Added by {event.added_by || "You"} ·{" "}
                    <span className="inline-flex items-center gap-0.5"><Lock size={10} /> {event.shared ? "Shared with care team" : "Private"}</span>
                </p>
            </div>
        </li>
    );
}

function JourneyTimeline() {
    const sid = getSessionId();
    const [events, setEvents] = useState(null);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        let on = true;
        api.get(`/journey/${sid}/timeline`).then(r => on && setEvents(r.data?.events || []))
            .catch(() => on && setEvents([]));
        return () => { on = false; };
    }, [sid]);

    const cats = useMemo(() => {
        const present = new Set((events || []).map(e => e.category));
        return ["all", ...Object.keys(CAT_META).filter(c => present.has(c))];
    }, [events]);
    const shown = (events || []).filter(e => filter === "all" || e.category === filter);

    return (
        <div className="space-y-5" data-testid="journey-timeline">
            <PageHero
                kicker="Our Journey · Timeline"
                kickerIcon={History}
                title="The story so far"
                subtitle="Every behavior note, medication change, appointment, and document — one continuous timeline. You never have to enter the same thing twice."
            />
            {cats.length > 2 && (
                <div className="flex gap-1.5 flex-wrap">
                    {cats.map(c => (
                        <button key={c} type="button" onClick={() => setFilter(c)} data-testid={`timeline-filter-${c}`}
                            className={`px-3 py-1.5 rounded-2xl text-xs font-semibold transition-colors ${filter === c ? "bg-teal text-white" : "bg-white border border-cream-dark text-[#4C5551]"}`}>
                            {c === "all" ? "Everything" : (CAT_META[c]?.label || c)}
                        </button>
                    ))}
                </div>
            )}
            {events === null ? (
                <div className="ml-card p-8 text-center text-sm text-[#6E7A74]">Loading the timeline…</div>
            ) : shown.length ? (
                <ul className="space-y-3">
                    {shown.map((e, i) => <TimelineRow key={`${e.id}-${i}`} event={e} />)}
                </ul>
            ) : (
                <div className="ml-card p-8 text-center" data-testid="timeline-empty">
                    <p className="text-sm text-[#4C5551]">Nothing here yet. Start by <Link to="/journey/behaviors" className="text-teal-dark underline">adding what happened today</Link>.</p>
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
const NEW_MED = { name: "", dose: "", frequency: "", purpose: "", prescriber: "", status: "active" };

function MedicationsSection() {
    const sid = getSessionId();
    const [doc, setDoc] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let on = true;
        api.get(`/patient-profile/${sid}`).then(r => on && setDoc({ ...r.data, medications: r.data.medications || [] }))
            .catch(() => on && setDoc({ medications: [] }));
        return () => { on = false; };
    }, [sid]);

    const meds = doc?.medications || [];
    const activeMeds = meds.filter(m => (m.status || "active") !== "stopped");
    const stoppedMeds = meds.filter(m => (m.status || "active") === "stopped");

    const setMed = (idx, field, value) => {
        setDoc(d => ({ ...d, medications: d.medications.map((m, i) => i === idx ? { ...m, [field]: value } : m) }));
    };
    const addMed = () => setDoc(d => ({ ...d, medications: [...(d.medications || []), { ...NEW_MED }] }));
    const removeMed = (idx) => setDoc(d => ({ ...d, medications: d.medications.filter((_, i) => i !== idx) }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/patient-profile/${sid}`, doc);
            const names = (doc.medications || []).map(m => m.name).filter(Boolean).slice(0, 5).join(", ");
            api.post(`/journey/${sid}/event`, {
                what: "Medication list updated",
                detail: names,
                category: "medication",
            }).catch(() => { });
            toast.success("Medications saved — this update was added to your Timeline.");
        } catch {
            toast.error("Couldn't save. Try again.");
        } finally {
            setSaving(false);
        }
    };

    const MedRow = ({ m, idx }) => (
        <div className="border border-cream-dark rounded-2xl p-4 bg-white" data-testid={`med-row-${idx}`}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
                <input className="ml-input !py-2 text-sm" placeholder="Medication name" value={m.name}
                    onChange={e => setMed(idx, "name", e.target.value)} data-testid={`med-name-${idx}`} />
                <input className="ml-input !py-2 text-sm" placeholder="Dose (e.g. 10 mg)" value={m.dose || ""}
                    onChange={e => setMed(idx, "dose", e.target.value)} />
                <input className="ml-input !py-2 text-sm" placeholder="How often" value={m.frequency || ""}
                    onChange={e => setMed(idx, "frequency", e.target.value)} />
                <input className="ml-input !py-2 text-sm" placeholder="What it's for" value={m.purpose || ""}
                    onChange={e => setMed(idx, "purpose", e.target.value)} />
                <input className="ml-input !py-2 text-sm" placeholder="Prescribed by" value={m.prescriber || ""}
                    onChange={e => setMed(idx, "prescriber", e.target.value)} />
            </div>
            <div className="mt-2.5 flex items-center justify-between">
                <button type="button" data-testid={`med-toggle-status-${idx}`}
                    onClick={() => setMed(idx, "status", (m.status || "active") === "stopped" ? "active" : "stopped")}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-2xl border transition-colors ${(m.status || "active") === "stopped" ? "bg-cream-dark text-[#4C5551] border-cream-dark" : "bg-teal-soft text-teal-dark border-teal/30"}`}>
                    {(m.status || "active") === "stopped" ? "Stopped — mark as current" : "Currently taking"}
                </button>
                <button type="button" onClick={() => removeMed(idx)} className="text-xs text-coral-dark inline-flex items-center gap-1 hover:underline" data-testid={`med-remove-${idx}`}>
                    <Trash2 size={13} /> Remove
                </button>
            </div>
        </div>
    );

    return (
        <div className="space-y-5" data-testid="journey-medications">
            <PageHero
                kicker="Our Journey · Medications"
                kickerIcon={Pill}
                title="Medications"
                subtitle="Keep the current list in one place — it flows into Appointment Prep and the Timeline automatically, so you never rewrite it at a doctor's office again."
                actions={
                    <button type="button" onClick={addMed} className="ml-btn-primary text-sm !min-h-[44px] !py-2.5" data-testid="med-add-btn">
                        <Plus size={16} strokeWidth={2} /> Add a medication
                    </button>
                }
            />

            {doc === null ? (
                <div className="ml-card p-8 text-center text-sm text-[#6E7A74]">Loading medications…</div>
            ) : (
                <>
                    {activeMeds.length === 0 && stoppedMeds.length === 0 && (
                        <div className="ml-card p-8 text-center" data-testid="meds-empty">
                            <p className="text-sm text-[#4C5551]">No medications tracked yet. Add the first one — even "as needed" medications count.</p>
                        </div>
                    )}
                    {meds.map((m, idx) => ((m.status || "active") !== "stopped" ? <MedRow key={idx} m={m} idx={idx} /> : null))}
                    {stoppedMeds.length > 0 && (
                        <details className="ml-card p-4 bg-white" data-testid="meds-stopped">
                            <summary className="text-sm font-semibold text-[#4C5551] cursor-pointer">Recently stopped ({stoppedMeds.length})</summary>
                            <div className="mt-3 space-y-3">
                                {meds.map((m, idx) => ((m.status || "active") === "stopped" ? <MedRow key={idx} m={m} idx={idx} /> : null))}
                            </div>
                        </details>
                    )}
                    {meds.length > 0 && (
                        <div className="flex items-center gap-3 flex-wrap">
                            <button type="button" onClick={save} disabled={saving} className="ml-btn-primary text-sm" data-testid="meds-save-btn">
                                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save medications
                            </button>
                            {doc.updated_at && <span className="text-xs text-[#6E7A74]">Last updated {fmtDate(doc.updated_at)}</span>}
                        </div>
                    )}
                    <p className="text-xs text-[#6E7A74] leading-relaxed" data-testid="meds-disclaimer">
                        Memory Lane helps you organize and understand medications — it never provides prescribing advice.
                        Questions about a dose or side effect? <Link to="/chat" className="text-teal-dark underline">Ask Lane</Link> to
                        prepare them for your next visit, or contact the prescriber.
                    </p>
                </>
            )}
        </div>
    );
}
